<template>
  <p v-if="showSignIn && !disableSignIn" class="mb-2">
    {{ $t.registerDialog.alreadyAMember }}
    <base-button
      class="underlined"
      @click="DialogSignIn.open({
        formLocation: 'modal:single:hero:none',
      })"
    >
      {{ $t.registerDialog.signIn }}
    </base-button>
  </p>
  <lazy-form-sign-in-social-login
    v-if="$feature.configSocialLogin.length"
    class="flex flex-col-reverse"
    mode="signup"
  />
  <div data-test-id="form-sign-up">
    <vf-notification
      v-if="notification.message"
      class="mb-4"
      data-scroll-el="sign-up-notification"
      :dismissible="false"
      :type="notification.type"
    >
      {{ notification.message }}
    </vf-notification>
    <template v-if="!compact">
      <h2 v-if="!insideDialog" :class="brandClasses.heading">
        {{ isLoyaltySignUp ? $t.loyaltySignUpForm.header : $t.createAccount }}
      </h2>
      <p v-if="$feature.enableLoyaltyFeatures" class="mb-4 mt-2 ">
        {{ isLoyaltySignUp ? $t.loyaltySignUpForm.subheader : $t.joinCommunity }}
      </p>
      <vf-form-hint class="mb-2">
        {{ $t.requiredField }}
      </vf-form-hint>
    </template>
    <base-form ref="formRef" class="grid gap-4" :form @submit="() => isLoyaltySignUp ? loyaltySubmit() : submit()">
      <template v-for="fieldName in fieldsOrder" :key="fieldName">
        <template v-if="!simplified">
          <vf-form-field
            v-if="fieldName === 'firstName'"
            v-slot="{ attrs }"
            name="firstName"
            :rule="[...getValidator('firstName', $t.firstName)]"
          >
            <vf-input
              v-model="form.firstName"
              v-bind="attrs"
              :disabled="!!(isFormPrepopulated && form.firstName)"
              name="firstName"
              required
            >
              {{ $t.firstName }}
            </vf-input>
          </vf-form-field>
          <vf-form-field
            v-if="fieldName === 'lastName'"
            v-slot="{ attrs }"
            name="lastName"
            :rule="[...getValidator('lastName', $t.lastName)]"
          >
            <vf-input
              v-model="form.lastName"
              v-bind="attrs"
              :disabled="!!(isFormPrepopulated && form.lastName)"
              name="lastName"
              required
            >
              {{ $t.lastName }}
            </vf-input>
          </vf-form-field>
          <vf-form-field
            v-if="fieldName === 'birthDate' && showBirthDateField"
            v-slot="{ attrs }"
            :hint="$t.dateFormatHint"
            name="birthDate"
            :rule="[
              ...(isBirthDateRequired ? [validateRequired($t.birthDate)] : []),
              validateDate(),
              validateMinDate(minBirthDate, $t.dobValidationMsg),
              validateMaxDate(maxBirthDate, replaceAll($t.dobAgeRestrictionMsg, { minimalAge })),
            ]"
          >
            <input-date
              v-model="form.birthDate"
              v-bind="attrs"
              :disabled="!!(isFormPrepopulated && form.birthDate)"
              :display-format="dateDisplayFormat"
              :required="isBirthDateRequired"
            >
              <template #end>
                <vf-toggletip placement="top-end">
                  <template #trigger>
                    <span class="sr-only">{{ $t.birthDate }}</span>
                    <vf-icon name="info" size="md" />
                  </template>
                  {{ $t.birthDateTooltip }}
                </vf-toggletip>
              </template>
            </input-date>
          </vf-form-field>
          <vf-form-field
            v-if="fieldName === 'zipCode'"
            v-slot="{ attrs }"
            name="postalCode"
            :rule="[...getValidator('postalCode', $t.zipCode)]"
          >
            <input-masked
              v-if="$feature.enableMaskedPostalCode && postalCodeFormat.mask"
              v-model="form.postalCode"
              v-bind="{ ...attrs, ...postalCodeFormat }"
              required
            >
              {{ $t.zipCode }}
            </input-masked>
            <vf-input v-else v-model="form.postalCode" v-bind="attrs" required>
              {{ $t.zipCode }}
            </vf-input>
          </vf-form-field>
          <template v-if="fieldName === 'phone' && showMobilePhoneField">
            <vf-form-field
              v-slot="{ attrs }"
              name="phone"
              :rule="[
                ...isMobilePhoneRequired ? [validateRequired($t.mobileNumber)] : [],
                validatePhone(form.phoneCode),
              ]"
            >
              <input-phone
                v-model="form.phone"
                v-model:code="form.phoneCode"
                v-bind="attrs"
                :hide-code-select="!mobilePhone.enableCountryCodeSelection && !isFormPrepopulated"
                :required="isMobilePhoneRequired"
                show-country-code
              >
                {{ $t.mobileNumber }}
                <template #end>
                  <vf-toggletip placement="top-start">
                    <template #trigger>
                      <span class="sr-only">{{ $t.mobileNumber }}</span>
                      <vf-icon name="info" size="md" />
                    </template>
                    {{ $t.mobileNumberTooltip }}
                  </vf-toggletip>
                </template>
              </input-phone>
            </vf-form-field>
            <vf-form-field
              v-if="showSmsSection"
              v-slot="{ attrs }"
              name="optInAccepted"
              :rule="isSmsOptInRequired && isPhoneValid ? [validateRequired('', $t.validators.requiredCheckbox)] : []"
            >
              <vf-checkbox
                v-model="form.optInAccepted"
                v-bind="attrs"
                class="text-md "
                :disabled="!isPhoneValid"
                :required="isSmsOptInRequired"
                :size="checkboxSizeSms"
              >
                <form-legal-disclaimer :content="$t.smsTerms" />
              </vf-checkbox>
            </vf-form-field>
          </template>
          <vf-form-field
            v-if="isGenderRequired && fieldName === 'gender'"
            v-slot="{ attrs }"
            name="gender"
            :rule="validateRequired('', $t.validators.requiredSelect)"
          >
            <vf-select
              v-model="form.gender"
              v-bind="attrs"
              :disabled="!!(isFormPrepopulated && form.gender)"
              :options="genderOptions"
              required
            >
              {{ $t.gender }}
            </vf-select>
          </vf-form-field>
        </template>
        <vf-form-field
          v-if="fieldName === 'email'"
          v-slot="{ attrs }"
          :hint="disableEmail ? undefined : $t.emailFormatHint"
          name="email"
          :rule="[...getValidator('email', $t.email), validateEmail()]"
        >
          <vf-input
            v-model="form.email"
            v-bind="attrs"
            :disabled="disableEmail || !!(isFormPrepopulated && form.email)"
            name="email"
            required
            type="email"
            @change="handleEmailChange"
          >
            {{ $t.email }}
          </vf-input>
        </vf-form-field>
        <vf-form-field
          v-if="!isLoyaltySignUp && fieldName === 'password'"
          v-slot="{ attrs }"
          class="space-y-2"
          name="password"
          :rule="!isLoyaltySignUp ? [validateRequired($t.password), validatePassword()] : []"
        >
          <input-password v-model="form.password" v-bind="attrs" aria-describedby="password-requirements" required>
            {{ $t.password }}
          </input-password>
        </vf-form-field>
      </template>
      <validator-password
        v-if="!isLoyaltySignUp"
        id="password-requirements"
        :class="brandClasses.passwordRequirements"
        :password="form.password"
      />
      <div class="ws-pre-wrap" :class="brandClasses.checkboxesWrapper">
        <template
          v-for="item in agreementCheckboxesVisible"
          :key="item.type"
        >
          <vf-form-field
            v-slot="{ attrs }"
            :name="item.type"
            :rule="('required' in item && item.required) ? [validateRequired('', $t.validators.requiredCheckbox)] : []"
          >
            <vf-checkbox v-model="form[item.type]" v-bind="attrs" :required="item.required" :size="checkboxSize">
              <form-legal-disclaimer
                :content="$t.brandAgreement[item.type]"
                :data-test-ids="{ 'loyalty-terms': 'form-sign-up-link-loyalty' }"
              />
            </vf-checkbox>
          </vf-form-field>
        </template>
      </div>
      <loyalty-benefits v-if="$feature.showLoyaltyBenefitsInSignUp">
        <template #loyalty-terms>
          <template
            v-for="item in benefitCheckboxesVisible"
            :key="item.type"
          >
            <vf-form-field
              v-slot="{ attrs }"
              :name="item.type"
              :rule="item.required ? validateRequired('', $t.validators.requiredCheckbox) : []"
            >
              <vf-checkbox v-model="form[item.type]" v-bind="attrs" :required="item.required" :size="checkboxSize">
                <form-legal-disclaimer
                  :content="$t.brandAgreement[item.type]"
                  :data-test-ids="{ 'loyalty-terms': 'form-sign-up-link-loyalty' }"
                />
              </vf-checkbox>
            </vf-form-field>
          </template>
        </template>
      </loyalty-benefits>
      <vf-button :class="brandClasses.submitButton" :loading :size="buttonSize" type="submit">
        {{ isLoyaltySignUp ? $t.loyaltySignUpForm.submit : $t.createAccountSubmit }}
      </vf-button>
      <div v-if="!isSignInToBuy && showSignInCta && insideDialog" class="flex items-center gap-2 text-sm">
        {{ $t.alreadyAMember }}
        <vf-link @click="openDialogSignIn">
          {{ $t.signIn }}
        </vf-link>
      </div>
      <!-- espot: below-sign-up -->
      <cms-section v-if="eSpot" class="pt-10 lg:mt-8 md:ml-4 md:mt-6" lazy-media :section="eSpot" />
    </base-form>
  </div>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { AgreementCheckbox } from '#types/config/components/form/signUp'
import type { FormLocation } from '#types/gtm'
import type { BaseNotification } from '#types/notification'
import { ApiErrorCode } from '#root/enums/api'

const {
  compact,
  disableEmail,
  disableSignIn,
  email,
  eSpot,
  formLocation = 'modal:single:sign-up:none',
  insideDialog,
  isLoyaltySignUp,
  isSignInToBuy,
  preventDialog,
  simplified,
  skipDialogInterests
} = defineProps<{
  compact?: boolean
  disableEmail?: boolean
  email?: string
  eSpot?: any
  formLocation?: FormLocation
  disableSignIn?: boolean
  insideDialog?: boolean
  isLoyaltySignUp?: boolean
  preventDialog?: boolean
  isSignInToBuy?: boolean
  showSignInCta?: boolean
  simplified?: boolean
  skipDialogInterests?: boolean
}>()

const emit = defineEmits<{
  submit: [newsletterConsent?: boolean]
  resolve: []
  signIn: []
}>()

const { load: loadRules, getValidator, formatters } = useAddressApiRules()
const { authentication, consumer } = useApi()
const {
  agreementCheckboxes: agreementCheckboxesConfig,
  brandClasses,
  buttonSize,
  checkboxSize,
  checkboxSizeSms,
  fieldsOrder,
  gender,
  isBirthDateRequired,
  isSmsOptInRequired,
  loyaltyAgreementCheckboxes,
  minimalAge: minimalAgeConfig,
  mobilePhone,
  showBirthDateField,
  showMobilePhoneField,
  showSmsSectionLocales,
  showSignIn
} = useAppConfig().components.form.signUp
const minimalAge: number = minimalAgeConfig[useCountryCode()] || minimalAgeConfig
const {
  isInterestsDependingOnNewsletter
} = useAppConfig().components.dialog.signUp
const { order } = useCheckoutStore()
const countryCode = useCountryCode()
const { DialogInterests, DialogSignIn } = useDialogsStore()
const locale = useLocale()
const {
  validateDate,
  validateEmail,
  validatePassword,
  validatePhone,
  validateRequired,
} = useLocalizedValidators()
const { getUserEventData } = useAnalytics()
const { $feature, $gtm, $t, $optimizely } = useNuxtApp()
const user = useUserStore()
const recaptcha = useRecaptcha()
const route = useRoute()

const agreementCheckboxesVisible = ref<AgreementCheckbox[]>([])
const benefitCheckboxesVisible = ref<AgreementCheckbox[]>([])
const isFormPrepopulated = ref(false)
const formRef = ref<InstanceType<typeof BaseFormType>>()
const loading = ref(true)
const minBirthDate = new Date(DATE_MIN)
const maxBirthDate = ref()
const dateDisplayFormat = useRegion() === 'EMEA' ? DATE_FORMAT_DD_MM_YYYY : DATE_FORMAT_MM_DD_YYYY
const genderOptions = gender?.options.map((item) => ({ value: item.value, label: $t.genderOptions[item.label] })) ?? []
const isGenderRequired = gender && (!gender.requiredLocales || gender.requiredLocales.includes(locale))
const isMobilePhoneRequired = mobilePhone?.requiredLocales?.includes(locale)
const showSmsSection = showSmsSectionLocales?.includes(locale)
const { billingAddress = {} as typeof order['billingAddress'] } = order

const {
  firstName: profileFirstName,
  lastName: profileLastName,
  email: profileEmail,
  phone: profilePhone,
  birthDate: profileBirthdate,
  gender: profileGender,
  postalCode: profilePostalCode,
} = user.details || {}

const notification = reactive<BaseNotification>({ message: '', type: '' })
const form = reactive({
  firstName: billingAddress.firstName || '',
  lastName: billingAddress.lastName || '',
  birthDate: '',
  phone: parseE164Phone(billingAddress.phone ?? '')?.[1] || '',
  phoneCode: getPhoneCodeByCountryCode(countryCode) || '',
  email: email || billingAddress.email || '',
  password: '',
  newsletter: false,
  loyaltyTerms: false,
  brandTerms: false,
  policy: false,
  postalCode: '',
  ...(isGenderRequired && { gender: '' }),
  ...isLoyaltySignUp && {
    firstName: profileFirstName,
    lastName: profileLastName,
    email: profileEmail,
    phone: parseE164Phone(profilePhone || '')?.[1] || '',
    birthDate: profileBirthdate,
    postalCode: profilePostalCode,
    ...(isGenderRequired && { gender: profileGender || '' })
  },
  optInAccepted: false
})

const postalCodeFormat = computed(() => formatters.value.postalCode || {})

const sortAgreementCheckboxes = (agreementCheckboxes: AgreementCheckbox[]) => {
  const items: AgreementCheckbox[] = []
  const itemsInBenefits: AgreementCheckbox[] = []

  const agreements = (isLoyaltySignUp && loyaltyAgreementCheckboxes) || agreementCheckboxes
  agreements.forEach((item: AgreementCheckbox) => {
    if (item.showCheckboxInBenefitsSection?.includes(locale))
      itemsInBenefits.push(item)
    else if (item.locales === undefined || item.locales.includes(locale))
      items.push(item)
  })
  agreementCheckboxesVisible.value = items
  benefitCheckboxesVisible.value = itemsInBenefits
}

// Retail Enrollment, will preload the consumer information for sign up form
onMounted(async () => {
  loadRules(countryCode.toLocaleLowerCase(), locale)
  /*
  * `consumerId` and `enrolledEmail` are typed as `LocationQueryValue | LocationQueryValue[]`.
  * `LocationQueryValue` is defined as `string | null`. We're crossing out null with an if,
  * and in our case it's impossible for those values to be an array.
  * To not make any TS gymnastics we simply ignore the error.
  * */
  const { consumerId, email: enrolledEmail } = route.query
  let agreementCheckboxes = agreementCheckboxesConfig
  if ([!isLoyaltySignUp, consumerId, enrolledEmail].every(Boolean)) {
    const { consumerDetails, enrollments } = await consumer.$retrieveUserData({
      consumerId: consumerId as string,
      email: enrolledEmail as string
    })

    const { birthDate, email, firstName, lastName, phone, gender } = consumerDetails
    const [phoneCode, enrolledPhone] = parseE164Phone(phone ?? '')
    form.birthDate = birthDate
    form.email = email
    form.firstName = firstName
    form.lastName = lastName
    form.phone = enrolledPhone
    form.phoneCode = phone ? phoneCode : getPhoneCodeByCountryCode(countryCode) || ''
    form.gender = gender
    isFormPrepopulated.value = true
    if (enrollments.some(({ type, doubleOptin }) => type === 'Loyalty' && doubleOptin)) {
      form.loyaltyTerms = true
      agreementCheckboxes = agreementCheckboxesConfig.filter(({ type }) => type !== 'loyaltyTerms')
    }
  }
  else if (isLoyaltySignUp) {
    isFormPrepopulated.value
        = [profileFirstName, profileLastName, profileEmail, profilePhone, profileBirthdate, profileGender].some(Boolean)
  }
  sortAgreementCheckboxes(agreementCheckboxes)

  $gtm.push('authForm.onImpression', 'Registration', formLocation)
  const hasNewsletter = (items: AgreementCheckbox[]) => items.some(({ type }) => type === 'newsletter')
  if (hasNewsletter(agreementCheckboxesVisible.value)
    || ($feature.showLoyaltyBenefitsInSignUp && hasNewsletter(benefitCheckboxesVisible.value))) {
    $gtm.push('newsletterForm.onImpression', formLocation)
  }

  const today = await getServerDate()
  maxBirthDate.value = new Date(today.getTime())
  maxBirthDate.value.setFullYear(today.getUTCFullYear() - minimalAge)
  loading.value = false

  form.email.length > 0 && (document.querySelector('[type="password"]') as HTMLElement)?.focus()
})

const handleEmailChange = () => {
  $gtm.push('authForm.onEmail', 'Registration', formLocation)
}

const getExtraLoyaltyAttrs = () => ({
  loyaltyEnrollment: 'Full',
  loyaltyNewsletterConsent: true
})

const isPhoneValid = computed(() => form.phone && !formRef.value?.fields.phone.invalid)

watch(isPhoneValid, (isValid) => {
  if (!isValid) {
    form.optInAccepted = false
  }
})

async function submit() {
  notification.message = ''
  loading.value = true

  try {
    const {
      phone,
      phoneCode,
      policy: isTermsConfirmed,
      newsletter: newsletterConsent,
      loyaltyTerms: loyaltyConsent,
      optInAccepted: smsNotificationConsent,
      birthDate,
      ...restParams
    } = form

    const formParams = simplified
      ? {
          email: form.email,
          password: form.password
        }
      : {
          ...(birthDate && { birthDate }),
          ...restParams,
          ...(phone && { phone: formatE164phone(phone, phoneCode), phoneCode }) // When the country doesn't require phone number and mobile field is blank
        }

    $gtm.push('authForm.onRegistrationSubmit', formLocation, 'ecom')
    $gtm.push('authForm.onLoginSubmit', formLocation, true)
    if (newsletterConsent) $gtm.push('newsletterForm.onSubmit', formLocation)

    const captchaResponse = await recaptcha.execute('register')
    await authentication.$signUp({
      // form data
      ...formParams,
      isTermsConfirmed,
      isTemporaryPassword: true,
      subscriptions: {
        newsletterConsent,
        loyaltyConsent,
        smsNotificationConsent,
        ...(loyaltyConsent && getExtraLoyaltyAttrs())
      },
      source: {
        store: route.query.storeNum ? route.query.storeNum.toString() : useSiteId(),
        acquisitionType: 'Registered',
        campaignName: route.query.enrollment_marketing_source as string
      },
      recaptcha_response: captchaResponse,
      ...(loyaltyConsent && !isTermsConfirmed && { termsConditionsAndPolicy: true })
    }, {
      onRequest: (context) => {
        if (route.query.leadSource) {
          context.options.headers.set('source', route.query.leadSource.toString())
        }
      }
    })

    notification.message = $t.accountCreated
    notification.type = 'success'

    // sign in the user after a successful registration
    const { login } = useAuthStore()
    const token = await recaptcha.execute('login')
    await login(form.email, form.password, token)

    notification.message = $t.youHaveSuccessfullyLoggedIn

    if (route.query.referral_code) {
      const xUsidCookie = useCookie('x-usid')
      await consumer.$getLoyaltyToken({
        usid: xUsidCookie.value || uuid(),
        referralCode: route.query.referral_code as string,
      })
    }

    await user.get()
    // moved it here so the Sign Up modal closes earlier
    emit('submit', newsletterConsent)

    const userEventData = await getUserEventData()
    $gtm.push('user.onLoadUserData', userEventData)
    $gtm.push('authForm.onRegistrationConfirmed', 'Confirmed', formLocation, 'ecom', 'Standard', false)
    $gtm.push('authForm.onLoginConfirmed', formLocation, 'Standard', true)
    $optimizely.dataReady(userEventData)
    if (newsletterConsent) $gtm.push('newsletterForm.onConfirmed', formLocation)

    if (skipDialogInterests || isSignInToBuy) return

    if ($feature.collectInterestsAtSignUp && (!isInterestsDependingOnNewsletter || newsletterConsent))
      DialogInterests.open()
  }
  catch (err) {
    assertApiError(err)

    switch (err.errorId) {
      case ApiErrorCode.LOGIN_IN_USE:
      case ApiErrorCode.LOGIN_IN_USE2:
        formRef.value!.invalidate('email', $t.apiMessages[err.errorId], true)
        break

      case ApiErrorCode.PHONE_NUMBER_ALREADY_IN_USE:
      case ApiErrorCode.PHONE_NUMBER_ALREADY_IN_USE2:
        formRef.value!.invalidate('phone', $t.apiMessages[err.errorId], true)
        break

        // in case of missing birthdate need to trigger validation on the form
      case ApiErrorCode.MISSING_BIRTHDATE:
        formRef.value?.validate()
        break

      default:
        notification.message = err.message
        notification.type = 'error'

        await nextTick()

        scrollToElement('sign-up-notification')
    }
  }
  finally {
    loading.value = false
  }
}

async function loyaltySubmit() {
  notification.message = ''
  loading.value = true

  try {
    const {
      firstName,
      lastName,
      phone,
      phoneCode,
      birthDate,
      gender
    } = form

    const data = {
      consumerProfile: {
        consumerDetails: {
          ...(!profileFirstName && { firstName }),
          ...(!profileLastName && { lastName }),
          ...(!profilePhone && phone && { phone: formatE164phone(phone, phoneCode) }),
          ...(!profileBirthdate && { birthDate }),
          ...(!profileGender && gender && { gender })
        },
        enrollments: [
          {
            type: 'Loyalty',
            optin: true,
            doubleOptin: true,
            region: useRegion(),
            channel: 'email'
          }
        ]
      }
    }

    await consumer.$updateProfile(data)
    await user.get()

    emit('submit')
  }
  catch (err) {
    assertApiError(err)
    // in case of missing birthdate need to trigger validation on the form
    if (err.errorId === ApiErrorCode.MISSING_BIRTHDATE) {
      formRef.value?.validate()
    }
    else {
      notification.message = err.message
      notification.type = 'error'
    }
  }
  finally {
    loading.value = false
  }
}

const openDialogSignIn = async () => {
  emit('signIn')

  if (!preventDialog) {
    DialogSignIn.open({
      formLocation
    })
    // When we open the sign-in, close the sign-up
    emit('resolve')
  }
}
</script>
